import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Calculator, Zap, CheckCircle2, Plus, Minus, X, Divide } from 'lucide-react';

interface Fraction {
  numerator: number;
  denominator: number;
}

interface CalculationStats {
  inputComplexity: number;
  outputComplexity: number;
  processingTime: number;
  operationsCount: number;
}

interface FractionResult {
  result: Fraction;
  decimal: number;
  percentage: string;
  simplified: Fraction;
  mixedNumber: {
    whole: number;
    fraction: Fraction;
  };
  steps: string[];
  stats: Omit<CalculationStats, 'operationsCount'>;
}

type Operation = 'add' | 'subtract' | 'multiply' | 'divide' | 'simplify' | 'compare';
type DisplayFormat = 'proper' | 'improper' | 'mixed' | 'decimal';

const FractionCalculatorTool: React.FC = () => {
  const [fraction1, setFraction1] = useState<Fraction>({ numerator: 1, denominator: 2 });
  const [fraction2, setFraction2] = useState<Fraction>({ numerator: 1, denominator: 3 });
  const [operation, setOperation] = useState<Operation>('add');
  const [displayFormat, setDisplayFormat] = useState<DisplayFormat>('mixed');
  const [autoSimplify, setAutoSimplify] = useState<boolean>(true);
  const [showSteps, setShowSteps] = useState<boolean>(true);
  const [results, setResults] = useState<FractionResult>({
    result: { numerator: 0, denominator: 1 },
    decimal: 0,
    percentage: '0%',
    simplified: { numerator: 0, denominator: 1 },
    mixedNumber: { whole: 0, fraction: { numerator: 0, denominator: 1 } },
    steps: [],
    stats: {
      inputComplexity: 0,
      outputComplexity: 0,
      processingTime: 0
    }
  });
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<CalculationStats>({
    inputComplexity: 0,
    outputComplexity: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Helper functions for fraction operations
  const gcd = useCallback((a: number, b: number): number => {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b !== 0) {
      [a, b] = [b, a % b];
    }
    return a;
  }, []);

  const lcm = useCallback((a: number, b: number): number => {
    return Math.abs(a * b) / gcd(a, b);
  }, [gcd]);

  const simplifyFraction = useCallback((fraction: Fraction): Fraction => {
    if (fraction.denominator === 0) {
      throw new Error('Division by zero');
    }
    
    const commonDivisor = gcd(fraction.numerator, fraction.denominator);
    let num = fraction.numerator / commonDivisor;
    let den = fraction.denominator / commonDivisor;
    
    // Keep negative sign in numerator
    if (den < 0) {
      num = -num;
      den = -den;
    }
    
    return { numerator: num, denominator: den };
  }, [gcd]);

  const toMixedNumber = useCallback((fraction: Fraction): { whole: number; fraction: Fraction } => {
    const simplified = simplifyFraction(fraction);
    const whole = Math.floor(Math.abs(simplified.numerator) / simplified.denominator);
    const remainder = Math.abs(simplified.numerator) % simplified.denominator;
    
    return {
      whole: simplified.numerator < 0 ? -whole : whole,
      fraction: { numerator: remainder, denominator: simplified.denominator }
    };
  }, [simplifyFraction]);

  const addFractions = useCallback((f1: Fraction, f2: Fraction): { result: Fraction; steps: string[] } => {
    const steps: string[] = [];
    steps.push(`Adding: ${f1.numerator}/${f1.denominator} + ${f2.numerator}/${f2.denominator}`);
    
    if (f1.denominator === f2.denominator) {
      steps.push(`Same denominators: (${f1.numerator} + ${f2.numerator})/${f1.denominator}`);
      const result = { numerator: f1.numerator + f2.numerator, denominator: f1.denominator };
      steps.push(`Result: ${result.numerator}/${result.denominator}`);
      return { result, steps };
    }
    
    const commonDen = lcm(f1.denominator, f2.denominator);
    const num1 = f1.numerator * (commonDen / f1.denominator);
    const num2 = f2.numerator * (commonDen / f2.denominator);
    
    steps.push(`Find LCD: ${commonDen}`);
    steps.push(`Convert: ${num1}/${commonDen} + ${num2}/${commonDen}`);
    steps.push(`Add numerators: (${num1} + ${num2})/${commonDen}`);
    
    const result = { numerator: num1 + num2, denominator: commonDen };
    steps.push(`Result: ${result.numerator}/${result.denominator}`);
    
    return { result, steps };
  }, [lcm]);

  const subtractFractions = useCallback((f1: Fraction, f2: Fraction): { result: Fraction; steps: string[] } => {
    const steps: string[] = [];
    steps.push(`Subtracting: ${f1.numerator}/${f1.denominator} - ${f2.numerator}/${f2.denominator}`);
    
    if (f1.denominator === f2.denominator) {
      steps.push(`Same denominators: (${f1.numerator} - ${f2.numerator})/${f1.denominator}`);
      const result = { numerator: f1.numerator - f2.numerator, denominator: f1.denominator };
      steps.push(`Result: ${result.numerator}/${result.denominator}`);
      return { result, steps };
    }
    
    const commonDen = lcm(f1.denominator, f2.denominator);
    const num1 = f1.numerator * (commonDen / f1.denominator);
    const num2 = f2.numerator * (commonDen / f2.denominator);
    
    steps.push(`Find LCD: ${commonDen}`);
    steps.push(`Convert: ${num1}/${commonDen} - ${num2}/${commonDen}`);
    steps.push(`Subtract numerators: (${num1} - ${num2})/${commonDen}`);
    
    const result = { numerator: num1 - num2, denominator: commonDen };
    steps.push(`Result: ${result.numerator}/${result.denominator}`);
    
    return { result, steps };
  }, [lcm]);

  const multiplyFractions = useCallback((f1: Fraction, f2: Fraction): { result: Fraction; steps: string[] } => {
    const steps: string[] = [];
    steps.push(`Multiplying: ${f1.numerator}/${f1.denominator} × ${f2.numerator}/${f2.denominator}`);
    steps.push(`Multiply numerators: ${f1.numerator} × ${f2.numerator} = ${f1.numerator * f2.numerator}`);
    steps.push(`Multiply denominators: ${f1.denominator} × ${f2.denominator} = ${f1.denominator * f2.denominator}`);
    
    const result = {
      numerator: f1.numerator * f2.numerator,
      denominator: f1.denominator * f2.denominator
    };
    steps.push(`Result: ${result.numerator}/${result.denominator}`);
    
    return { result, steps };
  }, []);

  const divideFractions = useCallback((f1: Fraction, f2: Fraction): { result: Fraction; steps: string[] } => {
    const steps: string[] = [];
    
    if (f2.numerator === 0) {
      throw new Error('Cannot divide by zero');
    }
    
    steps.push(`Dividing: ${f1.numerator}/${f1.denominator} ÷ ${f2.numerator}/${f2.denominator}`);
    steps.push(`Multiply by reciprocal: ${f1.numerator}/${f1.denominator} × ${f2.denominator}/${f2.numerator}`);
    steps.push(`Multiply numerators: ${f1.numerator} × ${f2.denominator} = ${f1.numerator * f2.denominator}`);
    steps.push(`Multiply denominators: ${f1.denominator} × ${f2.numerator} = ${f1.denominator * f2.numerator}`);
    
    const result = {
      numerator: f1.numerator * f2.denominator,
      denominator: f1.denominator * f2.numerator
    };
    steps.push(`Result: ${result.numerator}/${result.denominator}`);
    
    return { result, steps };
  }, []);

  const compareFractions = useCallback((f1: Fraction, f2: Fraction): { result: Fraction; steps: string[] } => {
    const steps: string[] = [];
    steps.push(`Comparing: ${f1.numerator}/${f1.denominator} vs ${f2.numerator}/${f2.denominator}`);
    
    const decimal1 = f1.numerator / f1.denominator;
    const decimal2 = f2.numerator / f2.denominator;
    
    steps.push(`Convert to decimals: ${decimal1.toFixed(6)} vs ${decimal2.toFixed(6)}`);
    
    let comparison: string;
    let resultNum: number;
    
    if (decimal1 > decimal2) {
      comparison = `${f1.numerator}/${f1.denominator} > ${f2.numerator}/${f2.denominator}`;
      resultNum = 1;
    } else if (decimal1 < decimal2) {
      comparison = `${f1.numerator}/${f1.denominator} < ${f2.numerator}/${f2.denominator}`;
      resultNum = -1;
    } else {
      comparison = `${f1.numerator}/${f1.denominator} = ${f2.numerator}/${f2.denominator}`;
      resultNum = 0;
    }
    
    steps.push(`Result: ${comparison}`);
    
    return { result: { numerator: resultNum, denominator: 1 }, steps };
  }, []);

  const calculateFractions = useCallback((): FractionResult => {
    const startTime = performance.now();
    
    try {
      if (fraction1.denominator === 0 || fraction2.denominator === 0) {
        throw new Error('Denominator cannot be zero');
      }

      let result: Fraction;
      let steps: string[] = [];

      switch (operation) {
        case 'add':
          ({ result, steps } = addFractions(fraction1, fraction2));
          break;
        case 'subtract':
          ({ result, steps } = subtractFractions(fraction1, fraction2));
          break;
        case 'multiply':
          ({ result, steps } = multiplyFractions(fraction1, fraction2));
          break;
        case 'divide':
          ({ result, steps } = divideFractions(fraction1, fraction2));
          break;
        case 'simplify':
          result = fraction1;
          steps = [`Simplifying: ${fraction1.numerator}/${fraction1.denominator}`];
          break;
        case 'compare':
          ({ result, steps } = compareFractions(fraction1, fraction2));
          break;
        default:
          throw new Error('Invalid operation');
      }

      const simplified = autoSimplify ? simplifyFraction(result) : result;
      if (autoSimplify && (simplified.numerator !== result.numerator || simplified.denominator !== result.denominator)) {
        const commonDivisor = gcd(result.numerator, result.denominator);
        steps.push(`Simplify by dividing by GCD(${Math.abs(result.numerator)}, ${result.denominator}) = ${commonDivisor}`);
        steps.push(`Simplified: ${simplified.numerator}/${simplified.denominator}`);
      }

      const decimal = simplified.denominator !== 0 ? simplified.numerator / simplified.denominator : 0;
      const percentage = `${(decimal * 100).toFixed(2)}%`;
      const mixedNumber = toMixedNumber(simplified);

      const endTime = performance.now();
      
      setErrorMessage('');
      
      return {
        result: simplified,
        decimal,
        percentage,
        simplified,
        mixedNumber,
        steps,
        stats: {
          inputComplexity: Math.max(Math.abs(fraction1.numerator), fraction1.denominator, Math.abs(fraction2.numerator), fraction2.denominator),
          outputComplexity: Math.max(Math.abs(simplified.numerator), simplified.denominator),
          processingTime: endTime - startTime
        }
      };
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Calculation error');
      return {
        result: { numerator: 0, denominator: 1 },
        decimal: 0,
        percentage: '0%',
        simplified: { numerator: 0, denominator: 1 },
        mixedNumber: { whole: 0, fraction: { numerator: 0, denominator: 1 } },
        steps: [],
        stats: {
          inputComplexity: 0,
          outputComplexity: 0,
          processingTime: performance.now() - startTime
        }
      };
    }
  }, [fraction1, fraction2, operation, autoSimplify, addFractions, subtractFractions, multiplyFractions, divideFractions, compareFractions, simplifyFraction, toMixedNumber, gcd]);

  useEffect(() => {
    const result = calculateFractions();
    setResults(result);
    setStats(prev => ({
      ...result.stats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [calculateFractions]);

  const formatFraction = useCallback((fraction: Fraction, format: DisplayFormat): string => {
    if (fraction.denominator === 0) return 'Undefined';
    if (fraction.numerator === 0) return '0';
    
    const simplified = simplifyFraction(fraction);
    
    switch (format) {
      case 'proper':
        if (Math.abs(simplified.numerator) >= simplified.denominator) {
          const mixed = toMixedNumber(simplified);
          return mixed.whole === 0 ? `${simplified.numerator}/${simplified.denominator}` : 
                 `${Math.abs(simplified.numerator)}/${simplified.denominator}`;
        }
        return `${simplified.numerator}/${simplified.denominator}`;
      
      case 'improper':
        return `${simplified.numerator}/${simplified.denominator}`;
      
      case 'mixed':
        const mixed = toMixedNumber(simplified);
        if (mixed.whole === 0) {
          return `${simplified.numerator}/${simplified.denominator}`;
        }
        if (mixed.fraction.numerator === 0) {
          return mixed.whole.toString();
        }
        return `${mixed.whole} ${mixed.fraction.numerator}/${mixed.fraction.denominator}`;
      
      case 'decimal':
        return (simplified.numerator / simplified.denominator).toFixed(6);
      
      default:
        return `${simplified.numerator}/${simplified.denominator}`;
    }
  }, [simplifyFraction, toMixedNumber]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (text: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(`${label} copied to clipboard!`);
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const content = `Fraction Calculation Results
Operation: ${operation.toUpperCase()}
Fraction 1: ${fraction1.numerator}/${fraction1.denominator}
Fraction 2: ${fraction2.numerator}/${fraction2.denominator}

Result: ${formatFraction(results.result, displayFormat)}
Decimal: ${results.decimal}
Percentage: ${results.percentage}
Mixed Number: ${formatFraction(results.result, 'mixed')}

Calculation Steps:
${results.steps.map((step, index) => `${index + 1}. ${step}`).join('\n')}

Processing Time: ${results.stats.processingTime.toFixed(2)}ms
Input Complexity: ${results.stats.inputComplexity}
Output Complexity: ${results.stats.outputComplexity}
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fraction-calculation.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          // Try to parse fractions from file
          const fractionRegex = /(\d+)\/(\d+)/g;
          const matches = Array.from(result.matchAll(fractionRegex));
          
          if (matches.length >= 1) {
            setFraction1({
              numerator: parseInt(matches[0][1]),
              denominator: parseInt(matches[0][2])
            });
            
            if (matches.length >= 2) {
              setFraction2({
                numerator: parseInt(matches[1][1]),
                denominator: parseInt(matches[1][2])
              });
            }
            
            showNotification('Fractions loaded from file!');
          } else {
            showNotification('No valid fractions found in file');
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setFraction1({ numerator: 1, denominator: 2 });
    setFraction2({ numerator: 1, denominator: 3 });
    setOperation('add');
    setResults({
      result: { numerator: 0, denominator: 1 },
      decimal: 0,
      percentage: '0%',
      simplified: { numerator: 0, denominator: 1 },
      mixedNumber: { whole: 0, fraction: { numerator: 0, denominator: 1 } },
      steps: [],
      stats: {
        inputComplexity: 0,
        outputComplexity: 0,
        processingTime: 0
      }
    });
    setStats({
      inputComplexity: 0,
      outputComplexity: 0,
      processingTime: 0,
      operationsCount: 0
    });
    setErrorMessage('');
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleFractions = (): void => {
    const samples = [
      [{ numerator: 3, denominator: 4 }, { numerator: 1, denominator: 8 }],
      [{ numerator: 2, denominator: 3 }, { numerator: 5, denominator: 6 }],
      [{ numerator: 7, denominator: 12 }, { numerator: 1, denominator: 4 }],
      [{ numerator: 5, denominator: 8 }, { numerator: 3, denominator: 16 }]
    ];
    
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setFraction1(randomSample[0]);
    setFraction2(randomSample[1]);
  };

  const generateRandomFractions = (): void => {
    const randomFraction = () => ({
      numerator: Math.floor(Math.random() * 20) + 1,
      denominator: Math.floor(Math.random() * 20) + 1
    });
    
    setFraction1(randomFraction());
    setFraction2(randomFraction());
  };

  const swapFractions = (): void => {
    const temp = fraction1;
    setFraction1(fraction2);
    setFraction2(temp);
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Fraction Calculator</h1>
              <p className="text-gray-300 text-sm">Perform operations on fractions with step-by-step solutions</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Input Section */}
        <div className="mb-6 bg-gray-800 rounded-lg p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
            {/* Fraction 1 */}
            <div>
              <label className="block text-white font-medium mb-2">First Fraction</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={fraction1.numerator}
                  onChange={(e) => setFraction1(prev => ({ ...prev, numerator: parseInt(e.target.value) || 0 }))}
                  className="flex-1 bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none text-center"
                  placeholder="Numerator"
                />
                <div className="text-white text-xl font-bold">/</div>
                <input
                  type="number"
                  min="1"
                  value={fraction2.denominator === 0 ? '' : fraction1.denominator}
                  onChange={(e) => setFraction1(prev => ({ ...prev, denominator: parseInt(e.target.value) || 1 }))}
                  className="flex-1 bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none text-center"
                  placeholder="Denominator"
                />
              </div>
              <div className="mt-2 text-center">
                <span className="text-2xl font-bold text-indigo-400">
                  {formatFraction(fraction1, displayFormat)}
                </span>
              </div>
            </div>

            {/* Operation */}
            <div>
              <label className="block text-white font-medium mb-2">Operation</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setOperation('add')}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all ${
                    operation === 'add' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  <span>Add</span>
                </button>
                <button
                  onClick={() => setOperation('subtract')}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all ${
                    operation === 'subtract' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <Minus className="w-4 h-4" />
                  <span>Subtract</span>
                </button>
                <button
                  onClick={() => setOperation('multiply')}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all ${
                    operation === 'multiply' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <X className="w-4 h-4" />
                  <span>Multiply</span>
                </button>
                <button
                  onClick={() => setOperation('divide')}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all ${
                    operation === 'divide' ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <Divide className="w-4 h-4" />
                  <span>Divide</span>
                </button>
              </div>
              
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  onClick={() => setOperation('simplify')}
                  className={`px-3 py-2 text-sm rounded-lg transition-all ${
                    operation === 'simplify' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Simplify
                </button>
                <button
                  onClick={() => setOperation('compare')}
                  className={`px-3 py-2 text-sm rounded-lg transition-all ${
                    operation === 'compare' ? 'bg-orange-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Compare
                </button>
              </div>
            </div>

            {/* Fraction 2 */}
            <div className={operation === 'simplify' ? 'opacity-50' : ''}>
              <label className="block text-white font-medium mb-2">
                {operation === 'simplify' ? 'Second Fraction (unused)' : 'Second Fraction'}
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={fraction2.numerator}
                  onChange={(e) => setFraction2(prev => ({ ...prev, numerator: parseInt(e.target.value) || 0 }))}
                  disabled={operation === 'simplify'}
                  className="flex-1 bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none text-center disabled:opacity-50"
                  placeholder="Numerator"
                />
                <div className="text-white text-xl font-bold">/</div>
                <input
                  type="number"
                  min="1"
                  value={fraction2.denominator === 0 ? '' : fraction2.denominator}
                  onChange={(e) => setFraction2(prev => ({ ...prev, denominator: parseInt(e.target.value) || 1 }))}
                  disabled={operation === 'simplify'}
                  className="flex-1 bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none text-center disabled:opacity-50"
                  placeholder="Denominator"
                />
              </div>
              <div className="mt-2 text-center">
                <span className="text-2xl font-bold text-purple-400">
                  {formatFraction(fraction2, displayFormat)}
                </span>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-white font-medium mb-2">Display Format</label>
              <select
                value={displayFormat}
                onChange={(e) => setDisplayFormat(e.target.value as DisplayFormat)}
                className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              >
                <option value="mixed">Mixed Number</option>
                <option value="improper">Improper Fraction</option>
                <option value="proper">Proper Fraction</option>
                <option value="decimal">Decimal</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={autoSimplify}
                  onChange={(e) => setAutoSimplify(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                />
                <span className="text-white">Auto Simplify</span>
              </label>
            </div>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={showSteps}
                  onChange={(e) => setShowSteps(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                />
                <span className="text-white">Show Steps</span>
              </label>
            </div>
          </div>

          {errorMessage && (
            <div className="mt-4 p-3 bg-red-900 border border-red-700 rounded-lg">
              <div className="text-red-300">{errorMessage}</div>
            </div>
          )}
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Calculation Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-indigo-400">{stats.inputComplexity}</div>
                <div className="text-gray-300 text-sm">Input Complexity</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.outputComplexity}</div>
                <div className="text-gray-300 text-sm">Output Complexity</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* Fraction Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Fraction Operations Guide</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Addition/Subtraction:</strong> Find common denominator, add/subtract numerators
                </div>
                <div className="text-gray-300">
                  <strong>Multiplication:</strong> Multiply numerators, multiply denominators
                </div>
                <div className="text-gray-300">
                  <strong>Division:</strong> Multiply by reciprocal of second fraction
                </div>
                <div className="text-gray-300">
                  <strong>Simplification:</strong> Divide by GCD of numerator and denominator
                </div>
                <div className="text-gray-300">
                  <strong>Mixed Numbers:</strong> Whole number plus proper fraction
                </div>
                <div className="text-gray-300">
                  <strong>Comparison:</strong> Convert to decimals or cross multiply
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        <div className="flex-1 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Results</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Main Result */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Result</h3>
                <button
                  onClick={() => copyToClipboard(formatFraction(results.result, displayFormat), 'Result')}
                  disabled={!results.result.numerator && !results.result.denominator}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-4 rounded border min-h-[100px] flex items-center justify-center">
                {results.result.numerator !== 0 || results.result.denominator !== 1 ? (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-indigo-400 mb-2">
                      {formatFraction(results.result, displayFormat)}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {displayFormat !== 'decimal' ? `≈ ${results.decimal.toFixed(4)}` : 'Exact decimal'}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">Perform a calculation to see results</div>
                )}
              </div>
            </div>

            {/* Decimal */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Decimal</h3>
                <button
                  onClick={() => copyToClipboard(results.decimal.toString(), 'Decimal')}
                  disabled={!results.decimal}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-4 rounded border min-h-[100px] flex items-center justify-center">
                {results.decimal !== 0 ? (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-400 mb-2">
                      {results.decimal.toFixed(6)}
                    </div>
                    <div className="text-gray-400 text-sm">
                      Rounded to 6 decimals
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">0.000000</div>
                )}
              </div>
            </div>

            {/* Percentage */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Percentage</h3>
                <button
                  onClick={() => copyToClipboard(results.percentage, 'Percentage')}
                  disabled={!results.percentage || results.percentage === '0%'}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-4 rounded border min-h-[100px] flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400 mb-2">
                    {results.percentage}
                  </div>
                  <div className="text-gray-400 text-sm">
                    As percentage
                  </div>
                </div>
              </div>
            </div>

            {/* Mixed Number */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Mixed Number</h3>
                <button
                  onClick={() => copyToClipboard(formatFraction(results.result, 'mixed'), 'Mixed Number')}
                  disabled={!results.result.numerator && !results.result.denominator}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-4 rounded border min-h-[100px] flex items-center justify-center">
                {results.result.numerator !== 0 || results.result.denominator !== 1 ? (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-400 mb-2">
                      {formatFraction(results.result, 'mixed')}
                    </div>
                    <div className="text-gray-400 text-sm">
                      Mixed form
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">0</div>
                )}
              </div>
            </div>
          </div>

          {/* Steps */}
          {showSteps && results.steps.length > 0 && (
            <div className="mt-6 bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-3">Solution Steps</h3>
              <div className="bg-gray-900 p-4 rounded border max-h-80 overflow-y-auto">
                <div className="space-y-2">
                  {results.steps.map((step, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <div className="text-gray-300 font-mono text-sm">{step}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleFractions}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Fractions
            </button>
            <button
              onClick={generateRandomFractions}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              Random Fractions
            </button>
            <button
              onClick={swapFractions}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
            >
              Swap Fractions
            </button>
            <button
              onClick={() => {
                setFraction1({ numerator: results.result.numerator, denominator: results.result.denominator });
                setFraction2({ numerator: 1, denominator: 1 });
              }}
              disabled={!results.result.numerator && !results.result.denominator}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            >
              Use Result as Input
            </button>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".txt,.csv,.json"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors text-sm flex items-center space-x-2">
                <Upload className="w-4 h-4" />
                <span>Upload File</span>
              </div>
            </label>
            <button
              onClick={downloadFile}
              disabled={!results.result.numerator && !results.result.denominator}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download Results</span>
            </button>
            <button
              onClick={clearAll}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Fraction Reference */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Common Fractions Reference</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 text-center">
            {[
              { fraction: '1/2', decimal: '0.5', percentage: '50%' },
              { fraction: '1/3', decimal: '0.333', percentage: '33.3%' },
              { fraction: '1/4', decimal: '0.25', percentage: '25%' },
              { fraction: '1/5', decimal: '0.2', percentage: '20%' },
              { fraction: '1/8', decimal: '0.125', percentage: '12.5%' },
              { fraction: '2/3', decimal: '0.667', percentage: '66.7%' },
              { fraction: '3/4', decimal: '0.75', percentage: '75%' },
              { fraction: '5/6', decimal: '0.833', percentage: '83.3%' }
            ].map(({ fraction, decimal, percentage }) => (
              <div 
                key={fraction} 
                className="bg-gray-700 p-2 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors"
                onClick={() => {
                  const [num, den] = fraction.split('/').map(n => parseInt(n));
                  setFraction1({ numerator: num, denominator: den });
                }}
              >
                <div className="text-lg font-bold text-indigo-400">{fraction}</div>
                <div className="text-gray-300 text-xs">{decimal}</div>
                <div className="text-gray-400 text-xs">{percentage}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FractionCalculatorTool;